'use client';

// Single-flight session refresh. Concurrent 401s (e.g. several React Query
// requests firing at once) trigger only one POST /api/auth/refresh.
let refreshPromise = null;

export async function tryRefresh() {
  if (refreshPromise) return refreshPromise;
  refreshPromise = fetch('/api/auth/refresh', { method: 'POST' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

function redirectToLogin() {
  if (typeof window !== 'undefined') {
    window.location.href = '/login';
  }
}

async function parseBody(res) {
  if (res.status === 204) return null;
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('json')) {
    return res.json().catch(() => ({}));
  }
  return res.text().catch(() => '');
}

function toError(res, body) {
  const message =
    (body && typeof body === 'object' && body.error) ||
    (typeof body === 'string' && body) ||
    `Request failed (${res.status})`;
  const err = new Error(message);
  err.status = res.status;
  err.body = body;
  return err;
}

/**
 * fetch() wrapper for authenticated JSON API calls.
 *
 * - On 401 it silently refreshes the session and retries the request once. If
 *   the refresh also fails it redirects to /login. This closes the gap where a
 *   client `fetch(...).then(r => r.json())` swallowed a 401 (the query looked
 *   "successful" with an {error} body) so the app kept letting the user act
 *   while logged out, only bouncing to /login on a full page reload.
 * - On any other non-2xx it throws an Error carrying `.status` and parsed
 *   `.body`, so callers (and the global handlers) can react.
 * - On success it returns parsed JSON (or null for 204 / non-JSON returns text).
 *
 * Not suitable for streaming or FormData bodies that cannot be replayed on the
 * retry; use raw fetch for those.
 */
export async function apiFetch(url, opts = {}) {
  let res = await fetch(url, opts);

  if (res.status === 401) {
    const refreshed = await tryRefresh();
    if (!refreshed) {
      redirectToLogin();
      throw toError(res, { error: 'Session expired' });
    }
    res = await fetch(url, opts);
    if (res.status === 401) {
      redirectToLogin();
      throw toError(res, { error: 'Session expired' });
    }
  }

  const body = await parseBody(res);
  if (!res.ok) throw toError(res, body);
  return body;
}
